import {
    type IProductFilters,
    type IProductListFilterKey,
    type IProductSortField,
    PRODUCT_SORT_FIELDS,
} from '@/schemas/catalog/productFilters'

/** Filtros vazios (listagem sem filtro, na ordem de exibição). */
export const EMPTY_PRODUCT_FILTERS: IProductFilters = Object.freeze({
    term: '',
    category: [],
    feature: [],
    color: [],
    size: [],
    seller: [],
    manufacturer: [],
    priceFrom: null,
    priceTo: null,
    order: null,
    orderBy: null,
}) as IProductFilters

const LIST_KEYS: IProductListFilterKey[] = ['category', 'feature', 'color', 'size', 'seller', 'manufacturer']

/** `searchParams` do server component (`{ chave: valor | valor[] }`) ou o `URLSearchParams` do client. */
export type ISearchParamsInput = URLSearchParams | Record<string, string | string[] | undefined>

function getAll(params: ISearchParamsInput, key: string): string[] {
    if (params instanceof URLSearchParams) return params.getAll(key)
    const value = params[key]
    if (value === undefined) return []
    return Array.isArray(value) ? value : [value]
}

/** Valores de uma chave. Aceita chave repetida (formato novo) e valores unidos por `+` (links antigos da loja). */
function values(params: ISearchParamsInput, key: string): string[] {
    return getAll(params, key)
        .flatMap((value) => value.split('+'))
        .map((value) => value.trim())
        .filter(Boolean)
}

function price(params: ISearchParamsInput, key: string): number | null {
    const raw = getAll(params, key)[0]
    if (raw === undefined || raw.trim() === '') return null
    const value = Number(raw.replace(',', '.'))
    return Number.isFinite(value) && value >= 0 ? value : null
}

/**
 * Lê os filtros da URL. `keyword` vale como `term` (o Navbar legado manda `/produtos?keyword=` e o filtro lia só
 * `term`, então a busca do Navbar não filtrava). Ordem, campo de ordenação e preço inválidos são ignorados.
 */
export function parseProductFilters(params: ISearchParamsInput): IProductFilters {
    const order = getAll(params, 'order')[0]
    const orderBy = getAll(params, 'orderBy')[0]
    const filters: IProductFilters = {
        ...EMPTY_PRODUCT_FILTERS,
        term: (getAll(params, 'term')[0] ?? getAll(params, 'keyword')[0] ?? '').trim(),
        priceFrom: price(params, 'priceFrom'),
        priceTo: price(params, 'priceTo'),
        order: order === 'asc' || order === 'desc' ? order : null,
        orderBy: (PRODUCT_SORT_FIELDS as readonly string[]).includes(orderBy ?? '')
            ? (orderBy as IProductSortField)
            : null,
    }
    for (const key of LIST_KEYS) filters[key] = values(params, key)
    return filters
}

/** `&`, `=` e `+` separam chaves e valores dentro do `term`: num valor, viram espaço. */
const clean = (value: string) => value.replace(/[&=+]/g, ' ').replace(/\s+/g, ' ').trim()

/**
 * Monta o parâmetro `term` de `product/list-products` no formato que a API lê (`ProductService.Parameters` no
 * backend): `?term=texto&feature=a+b&color=x+y&order=asc&orderBy=price&priceFrom=30&priceTo=40&size=P+M`. Sem filtro,
 * texto vazio. Com `order` e sem `orderBy`, ordena por preço, como a loja sempre fez. O texto vai cru: o cliente HTTP codifica
 * o parâmetro inteiro.
 */
export function buildProductListTerm(filters: IProductFilters): string {
    const parts: string[] = []
    const add = (key: string, list: string[]) => {
        const cleaned = list.map(clean).filter(Boolean)
        if (cleaned.length > 0) parts.push(`${key}=${cleaned.join('+')}`)
    }

    add('term', [filters.term])
    add('feature', filters.feature)
    add('category', filters.category)
    add('color', filters.color)
    if (filters.order) {
        parts.push(`order=${filters.order}`)
        parts.push(`orderBy=${filters.orderBy ?? 'price'}`)
    }
    if (filters.priceFrom !== null) parts.push(`priceFrom=${filters.priceFrom}`)
    if (filters.priceTo !== null) parts.push(`priceTo=${filters.priceTo}`)
    add('size', filters.size)
    add('seller', filters.seller)
    add('manufacturer', filters.manufacturer)

    return parts.length > 0 ? `?${parts.join('&')}` : ''
}

/** Objeto para o `useURLState().updateURL`: só os filtros preenchidos (listas viram chave repetida). */
export function productFiltersToURL(filters: IProductFilters): Record<string, string | number | string[]> {
    const url: Record<string, string | number | string[]> = {}
    if (filters.term) url.term = filters.term
    for (const key of LIST_KEYS) if (filters[key].length > 0) url[key] = filters[key]
    if (filters.priceFrom !== null) url.priceFrom = filters.priceFrom
    if (filters.priceTo !== null) url.priceTo = filters.priceTo
    if (filters.order) url.order = filters.order
    if (filters.orderBy) url.orderBy = filters.orderBy
    return url
}

export function hasActiveFilters(filters: IProductFilters): boolean {
    return Object.keys(productFiltersToURL(filters)).length > 0
}
